import { BlockStack, Box, InlineStack, LegacyStack, Text } from '@shopify/polaris'
import { useContext, useState } from 'react'
import useSWR from 'swr'

import { backendFetchResultData } from '../common/api'
import {
  anyWindow,
  formatMoney,
  queryString,
  useShopTimeZone,
  useSystemData,
} from '../common/helpers'
import { HelpIcon } from '../common/HelpIcon'
import { recentActivityEmptyData } from '../common/new-user-test-data'
import { SettingsContext } from '../settings-page/api'
import { useAdvancedDashboardFilters } from './advanced-filters'
import { AdvancedDasbordFilterSelectors } from './AdvancedDasbordFilterSelectors'
import { AdvancedDashboardFilterLabels } from './AdvancedDashboardFilterLabels'
import { BasicStatCard } from './BasicStats'
import { ChartCard, ChartData } from './ChartCard'
import { DashboardAnalyticsStartDateInfo } from './DashboardSettingsModal'
import { FinishedStatus, FinishedStatusSelector } from './FinishedStatusSelector'
import { Period, periodISODateRange, periodLabel, PeriodSelector } from './PeriodSelector'

export interface RecentActivityResponse {
  newSubscriptions: ChartData
  finishedSubscriptions: ChartData
  newOrders: ChartData
  revenue: ChartData
  revenuePerSubscription: number
  revenuePerOrder: number
}

const fetchRecentActivity = (_: any, query: string) => {
  if (anyWindow.newUserTest) {
    return Promise.resolve(recentActivityEmptyData)
  }

  const url = `/dashboard/recent-activity-charts?${query}`
  const data = backendFetchResultData<RecentActivityResponse>('GET', url)

  return data
}

interface RecentActivityProps {
  period: Period
  periodStart: string | null
  periodEnd: string | null
  setPeriod: (period: Period, period_start?: string, period_end?: string) => void
  showEnhancedReports: boolean
}

export const RecentActivity = ({
  period,
  periodStart,
  periodEnd,
  setPeriod,
  showEnhancedReports,
}: RecentActivityProps) => {
  const systemData = useSystemData()
  const userSettings = useContext(SettingsContext)

  const tz = useShopTimeZone()
  const advancedFilters = useAdvancedDashboardFilters()
  const [finishedStatuses, setFinishedStatuses] = useState<FinishedStatus[]>([])

  const currency = systemData?.shopData.currency ?? 'USD'

  const { data: recentActivityData, isValidating: loading } = useSWR<RecentActivityResponse>(
    [
      'recent-activity-charts',
      queryString({
        ...advancedFilters.filterParams,
        ...periodISODateRange(period, periodStart, periodEnd, tz),
        finishedStatuses: finishedStatuses.length > 0 ? finishedStatuses.join(',') : undefined,
      }),
      userSettings?.analytics_start_date,
    ],
    fetchRecentActivity,
    {
      revalidateOnFocus: false,
    }
  )

  const filterLabel = `${periodLabel(period, null, null)} ${
    advancedFilters.isFiltered ? ' - Filtered' : ''
  }`

  return (
    <LegacyStack vertical>
      <Box paddingInline={{ xs: '400', sm: '0', md: '0', lg: '0', xl: '0' }}>
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <Text variant="headingMd" as="h1">
              Recent activity
            </Text>
            <InlineStack gap="200" blockAlign="center">
              <AdvancedDasbordFilterSelectors
                filter={advancedFilters}
                disabled={!showEnhancedReports}
              />
              <PeriodSelector
                period={period}
                periodStart={periodStart}
                periodEnd={periodEnd}
                onChange={setPeriod}
              />
              {!showEnhancedReports && (
                <HelpIcon>
                  Upgrade to our Traction plan or higher to unlock premium filters.
                </HelpIcon>
              )}
            </InlineStack>
          </InlineStack>
          <DashboardAnalyticsStartDateInfo />
        </BlockStack>
        {advancedFilters.isFiltered && <AdvancedDashboardFilterLabels filter={advancedFilters} />}
      </Box>
      <LegacyStack distribution="fillEvenly">
        <ChartCard
          title="New subscriptions"
          tooltip="Number of new subscriptions created within the selected period"
          filterLabel={filterLabel}
          data={recentActivityData?.newSubscriptions}
          loading={loading}
        />
        <ChartCard
          title="Finished subscriptions"
          tooltip="Number of subscriptions finished within the selected period"
          filterLabel={filterLabel}
          data={recentActivityData?.finishedSubscriptions}
          loading={loading}
          customAction={
            <FinishedStatusSelector
              selectedStatuses={finishedStatuses}
              onChange={setFinishedStatuses}
            />
          }
        />
      </LegacyStack>
      <LegacyStack distribution="fillEvenly">
        <ChartCard
          title="Revenue"
          tooltip="Total value of orders containing subscription items, created within the selected period"
          filterLabel={filterLabel}
          data={recentActivityData?.revenue}
          valueFormatter={(val: number) => formatMoney(val, currency)}
          loading={loading}
        />
        <ChartCard
          title="New orders"
          tooltip="Number of orders created within the selected period"
          filterLabel={filterLabel}
          data={recentActivityData?.newOrders}
          loading={loading}
        />
      </LegacyStack>
      {showEnhancedReports && (
        <LegacyStack distribution="fillEvenly">
          <BasicStatCard
            title="Revenue per subscription"
            filterLabel={filterLabel}
            loading={!recentActivityData}
            tooltip="Total revenue divided by the number of subscriptions generating that revenue within the selected period"
          >
            {recentActivityData?.revenuePerSubscription !== null &&
              recentActivityData?.revenuePerSubscription !== undefined && (
                <Text variant="headingLg" as="p">
                  {formatMoney(recentActivityData.revenuePerSubscription, currency)}
                </Text>
              )}
          </BasicStatCard>
          <BasicStatCard
            title="Revenue per order"
            filterLabel={filterLabel}
            loading={!recentActivityData}
            tooltip="Total revenue divided by the number of orders generating that revenue within the selected period"
          >
            {recentActivityData?.revenuePerOrder !== null &&
              recentActivityData?.revenuePerOrder !== undefined && (
                <Text variant="headingLg" as="p">
                  {formatMoney(recentActivityData.revenuePerOrder, currency)}
                </Text>
              )}
          </BasicStatCard>
        </LegacyStack>
      )}
    </LegacyStack>
  )
}
